import useWindowSize from "@/hooks/useWindowSize";
import { Box, Link, Typography } from "@mui/material";
import React from "react";
import { Link as RouterLink } from "react-router-dom";
import { BREAKPOINT, getArtistPath, lc, trackClick, trackPlatformClick } from "./_helpers";
import { openMailingPopup } from "./mailing/mailingStorage";

interface ArtistData {
  name?: string;
  username?: string;
  image?: string;
  external_urls?: { spotify?: string };
  images?: Array<{ url: string }>;
}

interface Song {
  artist?: string;
  artistUsername?: string;
  artwork?: string;
  spotifyArtistURL?: string;
}

interface FooterProps {
  artistData?: ArtistData;
  song?: Song;
  adMode?: boolean;
  surface?: "default" | "artist" | "release";
}

const SpotifyIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
    <path d="M12 0C5.4 0 0 5.4 0 12s5.4 12 12 12 12-5.4 12-12S18.66 0 12 0zm5.521 17.34c-.24.359-.66.48-1.021.24-2.82-1.74-6.36-2.101-10.561-1.141-.418.122-.779-.179-.899-.539-.12-.421.18-.78.54-.9 4.56-1.021 8.52-.6 11.64 1.32.42.18.479.659.301 1.02zm1.44-3.3c-.301.42-.841.6-1.262.3-3.239-1.98-8.159-2.58-11.939-1.38-.479.12-1.02-.12-1.14-.6-.12-.48.12-1.021.6-1.141C9.6 9.9 15 10.561 18.72 12.84c.361.181.54.78.241 1.2zm.12-3.36C15.24 8.4 8.82 8.16 5.16 9.301c-.6.179-1.2-.181-1.38-.721-.18-.601.18-1.2.72-1.381 4.26-1.26 11.28-1.02 15.721 1.621.539.3.719 1.02.419 1.56-.299.421-1.02.599-1.559.3z" />
  </svg>
)

const YouTubeIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
    <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" />
  </svg>
)

declare const __APP_VERSION__: string;

const Footer: React.FC<FooterProps> = ({ artistData, song, adMode = false, surface = "default" }) => {
  const currentYear = new Date().getFullYear()
  const version = typeof __APP_VERSION__ !== "undefined" ? __APP_VERSION__ : "1.0.0"
  const artistName = artistData?.name || song?.artist || "Truevined"
  const artistUsername = artistData?.username || song?.artistUsername
  const artistImage = artistData?.image || artistData?.images?.[0]?.url
  const windowSize = useWindowSize();

  const spotifyUrl = artistData?.external_urls?.spotify || song?.spotifyArtistURL
  const youtubeUrl = `https://youtube.com/@${(artistUsername || artistName || "").toLowerCase().replace(/\s+/g, "")}`
  const isReleaseSurface = surface === "release";
  const isArtistSurface = surface === "artist";
  const footerBackgroundColor = isReleaseSurface ? "rgba(1, 1, 1, 0.035)" : isArtistSurface ? "var(--backgroundColor2)" : "var(--color5)";
  const footerTextColor = isReleaseSurface ? "var(--textColor)" : "var(--logoColor2)";
  const footerAccentColor = isReleaseSurface ? "var(--textColor)" : "var(--logoColor)";
  const footerHairline = `color-mix(in srgb, ${footerAccentColor} 20%, transparent)`;
  const artistRouteName = artistUsername || artistName;
  const footerLinks = [
    { label: "Songs", to: getArtistPath(artistRouteName, "/songs") },
    { label: "Albums", to: getArtistPath(artistRouteName, "/albums") },
    { label: "Videos", to: getArtistPath(artistRouteName, "/videos") },
    ...(lc(artistName) === "truevined"
      ? [
          { label: "Merch", to: "/merch", track: "merch:footer" },
          { label: "Games", to: "/games", track: "arcade:footer" },
          { label: "The Branches", to: getArtistPath(artistRouteName, "/branches") },
        ]
      : []),
  ];
  const footerLinkSx = {
    color: footerTextColor,
    fontFamily: "Poppins",
    fontSize: { xs: "14px", md: "13px" },
    fontWeight: 500,
    lineHeight: 1.4,
    letterSpacing: "0.09em",
    textTransform: "uppercase" as const,
    textDecoration: "none",
    opacity: 0.88,
    minHeight: "44px",
    display: "inline-flex",
    alignItems: "center",
    px: "4px",
    background: "none",
    border: 0,
    cursor: "pointer",
    transition: "opacity 150ms ease",
    "&:hover": { opacity: 1, textDecoration: "underline", textUnderlineOffset: "5px", textDecorationThickness: "1px" },
    "&:focus-visible": { outline: `2px solid ${footerAccentColor}`, outlineOffset: "2px", opacity: 1 },
  };

  return (
    <Box
      component="footer"
      sx={{
        width: "100%",
        backgroundColor: footerBackgroundColor,
        borderTop: `1px solid ${footerHairline}`,
        py: { xs: "2.25rem", md: "2.5rem" },
        px: { xs: "2rem", md: "3rem" },
        // mt: { xs: "4rem", md: "6rem" },
        position: "relative",
        zIndex: 1,
      }}
    >
      <Box
        sx={{
          maxWidth: "1400px",
          margin: "0 auto",
          display: "flex",
          flexDirection: { xs: "column", md: isReleaseSurface ? "column" : "row" },
          justifyContent: isReleaseSurface ? "center" : "space-between",
          alignItems: isReleaseSurface ? "center" : { xs: "center", md: "flex-end" },
          gap: isReleaseSurface ? { xs: "1rem", md: "0.65rem" } : { xs: "1.5rem", md: "2rem" },
        }}
      >
        {/* Artist Info Section - hidden in adMode */}
        {!adMode && (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: isReleaseSurface ? "center" : { xs: "center", md: "flex-start" },
              gap: isReleaseSurface ? "0.35rem" : "1rem",
            }}
          >
            {windowSize.width < BREAKPOINT && artistUsername && (
              <Link
                to={getArtistPath(artistUsername)}
                component={RouterLink}
                sx={{
                  textDecoration: "none",
                  display: "flex",
                  alignItems: "center",
                  gap: "1rem",
                }}
              >
                {artistImage && (
                  <Box
                    component="img"
                    src={artistImage}
                    alt={artistName}
                    sx={{
                      width: { xs: "48px", md: "56px" },
                      height: { xs: "48px", md: "56px" },
                      borderRadius: "50%",
                      objectFit: "cover",
                      border: "2px solid var(--logoColor2)",
                    }}
                  />
                )}

                <Typography
                  sx={{
                    color: footerAccentColor,
                    fontSize: "clamp(20px, 5.5vw, 26px)",
                    fontFamily: "Hanson",
                    fontWeight: "700",
                    "&:hover": {
                      color: footerTextColor,
                    },
                  }}
                >
                  {artistName}
                </Typography>
              </Link>
            )}
            <Box
              component="nav"
              aria-label="Site links"
              sx={{
                display: "flex",
                flexWrap: "wrap",
                justifyContent: isReleaseSurface ? "center" : { xs: "center", md: "flex-start" },
                columnGap: { xs: "1.5rem", md: "1.25rem" },
                rowGap: "0.25rem",
              }}
            >
              {footerLinks.map((link) => (
                <Link
                  key={link.to}
                  component={RouterLink}
                  to={link.to}
                  sx={footerLinkSx}
                  onClick={() => trackClick((link as { track?: string }).track ?? `footer-link:${link.label.toLowerCase()}`, { label: link.label, section: "footer", artist: artistName, href: link.to })}
                >
                  {link.label}
                </Link>
              ))}
              <Box
                component="button"
                type="button"
                onClick={() => {
                  trackClick("footer-join-the-list", { label: "Join the list", section: "footer", artist: artistName });
                  openMailingPopup();
                }}
                sx={footerLinkSx}
              >
                Join the list
              </Box>
            </Box>
            <Typography
              sx={{
                display: "flex",
                flexWrap: 'wrap',
                columnGap: '.5rem',
                color: footerTextColor,
                fontSize: { xs: "13px", md: "12px" },
                lineHeight: 1.55,
                letterSpacing: "0.01em",
                opacity: 0.72,
                fontFamily: "Poppins",
                fontWeight: "400",
                textAlign: isReleaseSurface ? "center" : { xs: "center", md: "left" },
              }}
            >
              <span>© {currentYear} {artistName}.</span> <span>All rights reserved.</span>
            </Typography>

            {/* Social Links */}
            {!isArtistSurface && !isReleaseSurface && (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: "4px",
                  mt: "0.5rem",
                  ml: { md: "-10px" },
                }}
              >
                {spotifyUrl && (
                  <Link
                    href={spotifyUrl}
                    onClick={() => {
                      trackClick("footer-social:spotify", { label: "Footer Spotify", section: "footer", artist: artistName, platform: "spotify", href: spotifyUrl });
                      trackPlatformClick("spotify", artistName);
                    }}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={`${artistName} on Spotify`}
                    sx={{
                      color: footerTextColor,
                      opacity: 0.78,
                      transition: "opacity 0.2s ease, transform 0.2s ease",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      minWidth: "44px",
                      minHeight: "44px",
                      "&:hover": {
                        color: "#1DB954",
                        opacity: 1,
                        transform: "scale(1.08)",
                      },
                    }}
                  >
                    <SpotifyIcon />
                  </Link>
                )}
                <Link
                  href={youtubeUrl}
                  onClick={() => {
                    trackClick("footer-social:youtube", { label: "Footer YouTube", section: "footer", artist: artistName, platform: "youtube", href: youtubeUrl });
                    trackPlatformClick("youtube", artistName);
                  }}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={`${artistName} on YouTube`}
                  sx={{
                    color: footerTextColor,
                    opacity: 0.78,
                    transition: "opacity 0.2s ease, transform 0.2s ease",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    minWidth: "44px",
                    minHeight: "44px",
                    "&:hover": {
                      color: "#FF0000",
                      opacity: 1,
                      transform: "scale(1.08)",
                    },
                  }}
                >
                  <YouTubeIcon />
                </Link>
              </Box>
            )}
          </Box>
        )}

        {/* Powered by Section */}
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: adMode || isReleaseSurface ? "center" : { xs: "center", md: "flex-end" },
            gap: "0.5rem",
            width: adMode || isReleaseSurface ? "100%" : "auto",
          }}
        >
          <Typography
            sx={{
              color: footerTextColor,
              fontSize: adMode ? { xs: "13px", md: "12px" } : { xs: "12px", md: "11px" },
              lineHeight: 1.55,
              letterSpacing: "0.02em",
              fontFamily: "Poppins",
              fontWeight: "400",
              textAlign: adMode || isReleaseSurface ? "center" : { xs: "center", md: "right" },
              mt: 0,
              whiteSpace: "nowrap",
              opacity: 0.72,
            }}
          >
            Powered by{" "}
            {adMode ? (
              <Box component="span" sx={{ color: footerTextColor, fontWeight: "700" }}>
                Truevined v{version}
              </Box>
            ) : (
              <Link
                href="https://truevined.com"
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  color: footerTextColor,
                  textDecoration: "none",
                  fontWeight: "600",
                  "&:hover": {
                    textDecoration: "underline",
                    textUnderlineOffset: "4px",
                  },
                }}
              >
                Truevined v{version}
              </Link>
            )}
          </Typography>
        </Box>
      </Box>
    </Box>
  )
}

export default Footer
